import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Image as ImageIcon, Upload, Plus, Trash2, Eye, EyeOff, 
  CheckCircle, XCircle, RefreshCw, Camera, Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BeforeAfterGallery = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    procedure_type: '',
    description: '',
    before_image_url: '',
    after_image_url: '',
    date_performed: '',
    is_public: false,
    consent_obtained: false,
    tags: []
  });
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/before-after`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast.error('Failed to load gallery');
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      if (type === 'before') setUploadingBefore(true);
      else setUploadingAfter(true);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/before-after/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (type === 'before') {
        setFormData({ ...formData, before_image_url: response.data.url });
      } else {
        setFormData({ ...formData, after_image_url: response.data.url });
      }

      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      if (type === 'before') setUploadingBefore(false);
      else setUploadingAfter(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_obtained) {
      toast.error('Photo consent must be obtained before adding to gallery');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/before-after`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Before/After entry added successfully!');
      setShowAddModal(false);
      setFormData({
        procedure_type: '',
        description: '',
        before_image_url: '',
        after_image_url: '',
        date_performed: '',
        is_public: false,
        consent_obtained: false,
        tags: []
      });
      loadEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const togglePublic = async (entryId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/before-after/${entryId}`,
        { is_public: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Entry ${!currentStatus ? 'published' : 'unpublished'}`);
      loadEntries();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this before/after entry?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/before-after/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      loadEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Before & After Gallery</h2>
          <p className="text-gray-600">Showcase your aesthetic treatment results</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={loadEntries} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Gallery Grid */}
      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No before/after entries yet</p>
            <Button onClick={() => setShowAddModal(true)} className="mt-4">
              Add Your First Entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {entries.map((entry) => (
            <Card key={entry.id} className="overflow-hidden">
              <CardContent className="p-0">
                {/* Images */}
                <div className="grid grid-cols-2">
                  <div className="relative">
                    <img
                      src={entry.before_image_url}
                      alt="Before"
                      className="w-full h-64 object-cover"
                    />
                    <Badge className="absolute top-2 left-2 bg-red-500 text-white border-0">
                      Before
                    </Badge>
                  </div>
                  <div className="relative">
                    <img
                      src={entry.after_image_url}
                      alt="After"
                      className="w-full h-64 object-cover"
                    />
                    <Badge className="absolute top-2 left-2 bg-green-500 text-white border-0">
                      After
                    </Badge>
                  </div>
                </div>

                {/* Details */}
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{entry.procedure_type}</h3>
                      {entry.date_performed && (
                        <p className="text-sm text-gray-500">
                          {new Date(entry.date_performed).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {entry.is_public ? (
                        <Badge className="bg-green-100 text-green-700 border-0">
                          <Eye className="h-3 w-3 mr-1" />
                          Public
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 border-0">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Private
                        </Badge>
                      )}
                      {entry.consent_obtained && (
                        <Badge className="bg-blue-100 text-blue-700 border-0">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Consent
                        </Badge>
                      )}
                    </div>
                  </div>

                  {entry.description && (
                    <p className="text-sm text-gray-600">{entry.description}</p>
                  )}

                  {entry.tags && entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {entry.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant={entry.is_public ? 'outline' : 'default'}
                      onClick={() => togglePublic(entry.id, entry.is_public)}
                      className="flex-1"
                    >
                      {entry.is_public ? (
                        <>
                          <EyeOff className="h-4 w-4 mr-1" />
                          Unpublish
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-1" />
                          Publish
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteEntry(entry.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Add Before/After Entry</CardTitle>
              <CardDescription>Upload treatment result photos</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Procedure Type *</Label>
                  <Input
                    required
                    value={formData.procedure_type}
                    onChange={(e) => setFormData({ ...formData, procedure_type: e.target.value })}
                    placeholder="e.g., Rhinoplasty, Breast Augmentation"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Before Photo *</Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      {formData.before_image_url ? (
                        <img src={formData.before_image_url} alt="Before" className="w-full h-32 object-cover rounded mb-2" />
                      ) : (
                        <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0], 'before')}
                        disabled={uploadingBefore}
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>After Photo *</Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      {formData.after_image_url ? (
                        <img src={formData.after_image_url} alt="After" className="w-full h-32 object-cover rounded mb-2" />
                      ) : (
                        <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0], 'after')}
                        disabled={uploadingAfter}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the treatment and results..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date Performed</Label>
                  <Input
                    type="date"
                    value={formData.date_performed}
                    onChange={(e) => setFormData({ ...formData, date_performed: e.target.value })}
                  />
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consent_obtained}
                      onChange={(e) => setFormData({ ...formData, consent_obtained: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">
                      <CheckCircle className="inline h-4 w-4 mr-1 text-green-600" />
                      Patient consent obtained for photo use *
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">
                      <Eye className="inline h-4 w-4 mr-1 text-blue-600" />
                      Make public (show on website)
                    </span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={!formData.before_image_url || !formData.after_image_url || uploadingBefore || uploadingAfter}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                  >
                    Add Entry
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BeforeAfterGallery;
